import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useState,
} from 'react';
import { Patient } from '../domain/patient/patient';
import {
    createPatientSession,
    PatientSession,
} from '../domain/session/PatientSession';
import { setActiveSession } from '../domain/session/activeSession';
import { useAppServicesOrNull } from './AppServicesContext';

const SessionContext = createContext<PatientSession | null>(null);

interface Props {
    patient: Patient | null;
    children: ReactNode;
}

export function PatientSessionProvider({ patient, children }: Props) {
    // Null until the engine initializes — this provider mounts from app
    // start (tree shape must not depend on async engine state), so the
    // session is created only once both patient and services exist.
    const appServices = useAppServicesOrNull();
    const [session, setSession] = useState<PatientSession | null>(null);

    useEffect(() => {
        if (!patient || !appServices) {
            setSession(null);
            setActiveSession(null);
            return;
        }
        const s = createPatientSession(appServices, patient);
        setSession(s);
        setActiveSession(s);
        return () => {
            setActiveSession(null);
            s.dispose();
        };
    }, [patient, appServices]);

    return (
        <SessionContext.Provider value={session}>
            {children}
        </SessionContext.Provider>
    );
}

export function useSession(): PatientSession {
    const ctx = useContext(SessionContext);
    if (!ctx) {
        throw new Error(
            'useSession must be used inside PatientSessionProvider with a loaded patient'
        );
    }
    return ctx;
}

export function useSessionOrNull(): PatientSession | null {
    return useContext(SessionContext);
}
